<template>
  <div ref="filterRef" :class="$style.root">
    <div v-show="showResetFilter" :class="$style.filter_control">
      <PagePartsReviewListFilterTagList
        :sex-item="localSexItem"
        :height-item="localHeightItem"
        :weight-item="localWeightItem"
        :foot-item="localFootItem"
        @remove-tag="removeTag"
      />
      <AtomsConsumerButton :class="$style.clear" @click="resetFilter"> 絞り込みを解除 </AtomsConsumerButton>
    </div>
    <div :class="$style.filter_head">
      <div :class="$style.filter_count">{{ reviewCount }}件</div>
      <!-- Todo: sort UIのコンポーネント化 -->
      <div :class="$style.reviews_sort">
        <AtomsConsumerButton :class="$style.sort_button" @click="emit('update:openSort', !isOpenSort)">
          {{ getSortReviewValue(localSort) }}
        </AtomsConsumerButton>
        <div v-show="isOpenSort" :class="$style.sort_accordion">
          <AtomsConsumerRadioButton
            v-for="[sortKey, sortLabel] in Object.entries(sortReviewOptions)"
            :key="sortKey"
            :checked="localSort"
            :name="sortKey"
            :value="sortKey"
            @change="(v) => (localSort = v)"
            @click="emit('update:openSort', false)"
          >
            {{ sortLabel }}
          </AtomsConsumerRadioButton>
        </div>
      </div>
    </div>
    <div :class="$style.quick_filter">
      <div :class="$style.scroll_area">
        <div :class="$style.filter_list">
          <AtomsConsumerButton variant="secondary" @click="() => toggleCategoryAccordion('sex')">
            性別
            <AtomsConsumerImage src="/images/icon/arrow-down.svg" alt="arrow_down" :width="12" :height="6" />
          </AtomsConsumerButton>
          <div v-if="!isFootCategory" :class="$style.filter_list">
            <AtomsConsumerButton variant="secondary" @click="() => toggleCategoryAccordion('height')">
              身長
              <AtomsConsumerImage src="/images/icon/arrow-down.svg" alt="arrow_down" :width="12" :height="6" />
            </AtomsConsumerButton>
            <AtomsConsumerButton variant="secondary" @click="() => toggleCategoryAccordion('weight')">
              体重
              <AtomsConsumerImage src="/images/icon/arrow-down.svg" alt="arrow_down" :width="12" :height="6" />
            </AtomsConsumerButton>
          </div>
          <div v-else :class="$style.filter_list">
            <AtomsConsumerButton variant="secondary" @click="() => toggleCategoryAccordion('foot')">
              足のサイズ
              <AtomsConsumerImage src="/images/icon/arrow-down.svg" alt="arrow_down" :width="12" :height="6" />
            </AtomsConsumerButton>
          </div>
        </div>
      </div>
    </div>
    <div v-show="isOpenFilter && filterCategory" :class="$style.category_accordion">
      <AtomsConsumerButton :class="$style.close" @click="toggleCategoryAccordion()" />
      <PagePartsReviewListFilterCheckBoxList v-show="filterCategory === 'sex'" title="性別" :items="localSexItem" />
      <PagePartsReviewListFilterCheckBoxList
        v-show="filterCategory === 'height'"
        title="身長"
        :items="localHeightItem"
      />
      <PagePartsReviewListFilterCheckBoxList
        v-show="filterCategory === 'weight'"
        title="体重"
        :items="localWeightItem"
      />
      <PagePartsReviewListFilterCheckBoxList
        v-show="filterCategory === 'foot'"
        title="足のサイズ"
        :items="localFootItem"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { SortSearchReviewInput } from "~/types/type.generated"
import type { CommonCheckBoxItem, FilterCategoryType } from "~/types/review"

/** 並び替えの選択肢一覧 */
const SORT_REVIEW: { [key in SortSearchReviewInput]: string } = {
  [SortSearchReviewInput.CreatedUnixtimeDesc]: "新着順",
  [SortSearchReviewInput.HeightAsc]: "身長が低い順",
  [SortSearchReviewInput.HeightDesc]: "身長が高い順",
  [SortSearchReviewInput.WeightAsc]: "体重が軽い順",
  [SortSearchReviewInput.WeightDesc]: "体重が重い順",
  [SortSearchReviewInput.FootAsc]: "足のサイズが小さい順",
  [SortSearchReviewInput.FootDesc]: "足のサイズが大きい順",
}

const props = defineProps<{
  sort: SortSearchReviewInput
  sexItem: CommonCheckBoxItem[]
  heightItem: CommonCheckBoxItem[]
  weightItem: CommonCheckBoxItem[]
  footItem: CommonCheckBoxItem[]
  isOpenSort: boolean
  reviewCount: number
  isFootCategory: boolean
}>()

const emit = defineEmits<{
  (e: "update:sort", value: SortSearchReviewInput): void
  (e: "update:sexItem", value: CommonCheckBoxItem[]): void
  (e: "update:heightItem", value: CommonCheckBoxItem[]): void
  (e: "update:weightItem", value: CommonCheckBoxItem[]): void
  (e: "update:footItem", value: CommonCheckBoxItem[]): void
  (e: "update:openSort", isOpenSort: boolean): void
}>()

const { y } = useWindowScroll()
const { width: windowWidth } = useWindowSize()
const md = computed(() => windowWidth.value >= 768)
const isOpenFilter = ref(false)
/** undefined は未選択 */
const filterCategory = ref<FilterCategoryType>()
const filterRef = ref<HTMLElement>()

const localSort = computed({
  get: () => props.sort,
  set: (value) => {
    emit("update:sort", value)
  },
})
const localSexItem = ref<CommonCheckBoxItem[]>([...props.sexItem])
const localHeightItem = ref<CommonCheckBoxItem[]>([...props.heightItem])
const localWeightItem = ref<CommonCheckBoxItem[]>([...props.weightItem])
const localFootItem = ref<CommonCheckBoxItem[]>([...props.footItem])

/** Sortの選択肢をFOOT系カテゴリ商品かどうかで出し分ける */
const sortReviewOptions = computed(() => {
  if (props.isFootCategory) {
    return {
      [SortSearchReviewInput.CreatedUnixtimeDesc]: SORT_REVIEW[SortSearchReviewInput.CreatedUnixtimeDesc],
      [SortSearchReviewInput.FootAsc]: SORT_REVIEW[SortSearchReviewInput.FootAsc],
      [SortSearchReviewInput.FootDesc]: SORT_REVIEW[SortSearchReviewInput.FootDesc],
    }
  }
  return {
    [SortSearchReviewInput.CreatedUnixtimeDesc]: SORT_REVIEW[SortSearchReviewInput.CreatedUnixtimeDesc],
    [SortSearchReviewInput.HeightAsc]: SORT_REVIEW[SortSearchReviewInput.HeightAsc],
    [SortSearchReviewInput.HeightDesc]: SORT_REVIEW[SortSearchReviewInput.HeightDesc],
    [SortSearchReviewInput.WeightAsc]: SORT_REVIEW[SortSearchReviewInput.WeightAsc],
    [SortSearchReviewInput.WeightDesc]: SORT_REVIEW[SortSearchReviewInput.WeightDesc],
  }
})

const toggleCategoryAccordion = (category?: FilterCategoryType) => {
  const prevCategory = filterCategory.value
  if (category) filterCategory.value = category
  isOpenFilter.value =
    filterCategory.value === prevCategory || !isOpenFilter.value ? !isOpenFilter.value : isOpenFilter.value
}

const showResetFilter = computed(() => {
  return (
    localSexItem.value.some((item) => item.checked) ||
    localHeightItem.value.some((item) => item.checked) ||
    localWeightItem.value.some((item) => item.checked) ||
    localFootItem.value.some((item) => item.checked)
  )
})

const setFilterPosition = (): void => {
  const filterElement = filterRef.value
  const headerElement = document.getElementById("site-header-id")
  if (!filterElement || !headerElement) return
  const headerHeight = headerElement.clientHeight
  headerElement.classList.contains("hide")
    ? (filterElement.style.top = "0")
    : (filterElement.style.top = headerHeight + "px")
}
/** Filterの一括解除 */
const resetFilter = (): void => {
  ;[localSexItem, localHeightItem, localWeightItem, localFootItem].forEach((itemList) => {
    itemList.value.forEach((item) => {
      item.checked = false
    })
  })
  isOpenFilter.value = false
}
/** タグの削除時のイベントリスナー */
const removeTag = (target: FilterCategoryType, itemValue: string): void => {
  const targetItems = {
    sex: localSexItem,
    height: localHeightItem,
    weight: localWeightItem,
    foot: localFootItem,
  }
  targetItems[target].value.forEach((item) => {
    if (item.value === itemValue) item.checked = false
  })
}

/** sortの型ガード */
const isLimitedSortFindManyReviewInput = (value: SortSearchReviewInput): value is SortSearchReviewInput => {
  return Object.keys(SORT_REVIEW).includes(value)
}
const getSortReviewValue = (value: SortSearchReviewInput): string => {
  if (!isLimitedSortFindManyReviewInput(value)) return ""
  return SORT_REVIEW[value]
}

watch(y, () => {
  if (md.value) return
  setFilterPosition()
})
watch(
  () => localSexItem.value,
  (value) => {
    emit("update:sexItem", value)
  },
  { deep: true },
)
watch(
  () => localHeightItem.value,
  (value) => {
    emit("update:heightItem", value)
  },
  { deep: true },
)
watch(
  () => localWeightItem.value,
  (value) => {
    emit("update:weightItem", value)
  },
  { deep: true },
)
watch(
  () => localFootItem.value,
  (value) => {
    emit("update:footItem", value)
  },
  { deep: true },
)
</script>

<style lang="scss" module>
.root {
  transition: 0.4s ease-in-out;
  background: rgba($white, 0.9);
  backdrop-filter: blur(10px);
  .filter_control {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.25rem;
  }
  .filter_head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.5rem 0 0.5rem 0.5rem;
    white-space: nowrap;
    border-top: 1px solid $primary-20;
    border-bottom: 1px solid $primary-20;
    .reviews_sort {
      position: relative;
      font-size: $font-size-14;
      font-weight: bold;
      min-width: 6.5rem;

      .sort_button {
        @include sub-font;
        position: relative;
        padding: 0 0 0 1.5rem;
        font-size: $font-size-13;
        color: $black;
        cursor: pointer;
        text-decoration: underline;
        &:hover {
          opacity: 0.7;
        }
        &:after {
          position: absolute;
          top: 50%;
          left: 0;
          width: 14px;
          height: 16px;
          content: "";
          background: url("/images/common/arrow/sort.svg");
          background-size: contain;
          transform: translateY(-50%);
        }
      }

      .sort_accordion {
        position: absolute;
        top: 2rem;
        right: -0.5rem;
        z-index: 10;
        width: 85vw;
        padding: 1rem;
        background-color: $white;
        filter: drop-shadow(0 0.25rem 0.25rem rgba($black, 0.1));
        @include md {
          right: 0;
          width: 20rem;
        }
        :global {
          label {
            padding: 0.5rem;
            border-bottom: 1px solid $primary-20;
            flex-direction: row-reverse;
            justify-content: space-between;
            &:last-child {
              border-bottom: none;
            }
            input {
              border: none;
              &::after,
              &::before:not(:checked) {
                display: none;
              }
              // チェックアイコン
              &:checked {
                font-weight: bold;
                &::before {
                  top: calc(50% - 0.375rem);
                  right: 0;
                  left: auto;
                  transform: rotate(50deg);
                  width: 0.45rem;
                  height: 0.75rem;
                  border-radius: 0;
                  background: none;
                  border-right: 2px solid $secondary;
                  border-bottom: 2px solid $secondary;
                  content: "";
                }
              }
            }
          }
        }
      }
    }
  }
  .filter_button {
    @include sub-font;
    text-decoration: underline;
  }
  .quick_filter {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.5rem 0 0.5rem 0.5rem;
    white-space: nowrap;
    .scroll_area {
      overflow-x: auto;
      .filter_list {
        display: flex;
        gap: 0.5rem;
        white-space: nowrap;
        :global(button) {
          @include sub-font;
          color: $text-black;
          font-size: $font-size-12;
          padding: 0.375rem 0.875rem !important;
          border-radius: 1.5rem !important;
          &:focus {
            box-shadow: none;
          }
        }
      }
    }
  }
  .category_accordion {
    position: relative;
    padding: 1rem;
    border-bottom: 1px solid $primary-20;
    p {
      font-size: $font-size-16;
      margin-bottom: 0.5rem;
    }
    .close {
      position: absolute;
      top: 1rem;
      right: 1rem;
      padding: 15px;
      cursor: pointer;
      &::before,
      &::after {
        position: absolute;
        top: 14px;
        left: 0px;
        width: 1.5rem;
        height: 1px;
        content: "";
        background: $primary;
      }
      &::before {
        transform: rotate(45deg);
      }
      &::after {
        transform: rotate(-45deg);
      }
    }
  }
}
</style>
